import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { useSeo, setJsonLd } from "@/hooks/useSeo";
import { animeList } from "@/mocks/anime";

const tabs = [
  { key: "watch", label: "在线观看", icon: "ri-play-circle-line" },
  { key: "download", label: "高清下载", icon: "ri-download-2-line" },
];

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"watch" | "download">("watch");
  const [quality, setQuality] = useState("1080P");
  const [copied, setCopied] = useState(false);

  const anime = animeList.find((a) => a.id === id);

  useSeo({
    title: anime ? `${anime.title} 免费在线观看全集高清下载 - ${anime.year}${anime.category}番剧 | 奶水动漫` : "番剧详情 | 奶水动漫",
    description: anime
      ? `${anime.title}（${anime.year}）是${anime.category}类型动画，豆瓣口碑${anime.score.toFixed(1)}分，共${anime.episodes}集。奶水动漫提供${anime.title}免费在线观看全集与高清下载资源。${anime.desc}`
      : "番剧详情页",
    keywords: `奶水动漫,${anime?.title ?? ""},${anime?.category ?? ""}动画,动漫在线,动漫下载`,
    type: "article",
  });

  useEffect(() => {
    setTab("watch");
    setCopied(false);
  }, [id]);

  useEffect(() => {
    if (!anime) return;
    setJsonLd("ld-article", {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: `${anime.title} 免费在线观看全集高清下载`,
      description: anime.desc,
      image: [anime.cover],
      datePublished: `${anime.year}-01-01`,
      author: { "@type": "Organization", name: "奶水动漫" },
    });
  }, [anime]);

  if (!anime) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="container-site flex flex-col items-center justify-center py-32 text-center">
          <i className="ri-emotion-sad-line text-6xl text-background-700" />
          <h1 className="mt-5 font-heading text-2xl text-foreground-100">该番剧不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">返回列表挑选其他热门动画吧</p>
          <button onClick={() => navigate("/list")} className="btn-primary mt-6">回到番剧列表</button>
        </main>
        <Footer />
      </div>
    );
  }

  const related = animeList.filter((a) => a.category === anime.category && a.id !== anime.id).slice(0, 4);
  const fallback = related.length ? related : animeList.filter((a) => a.id !== anime.id).slice(0, 4);
  const episodes = Array.from({ length: anime.episodes }, (_, i) => i + 1);

  const copyLink = () => {
    navigator.clipboard?.writeText(`https://fullflick.github.io/download/${anime.id}/${quality}`).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main className="pt-16">
        <section className="relative overflow-hidden">
          <img src={anime.cover} alt={`${anime.title} 背景图`} title={`${anime.title} 全集资源`} className="absolute inset-0 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/50" />

          <div className="container-site relative z-10 py-10 md:py-14">
            <nav className="mb-5 flex items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/list" className="transition-colors hover:text-primary-400">全部番剧</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={`/list?cat=${encodeURIComponent(anime.category)}`} className="transition-colors hover:text-primary-400">{anime.category}</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-300">{anime.title}</span>
            </nav>

            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              <img
                src={anime.cover}
                alt={`${anime.title} 高清封面`}
                title={`${anime.title} 免费在线观看全集`}
                className="h-56 w-full shrink-0 rounded-lg border border-background-700 object-cover object-top sm:h-64 sm:w-44"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-500/15 px-2 py-0.5 text-xs font-semibold text-primary-400">{anime.category}</span>
                  <span className="rounded-md bg-secondary-500/10 px-2 py-0.5 text-xs text-secondary-300">{anime.status}</span>
                  <span className="flex items-center gap-1 text-sm font-bold text-accent-400">
                    <i className="ri-star-fill" /> {anime.score.toFixed(1)}
                  </span>
                </div>
                <h1 className="mt-3 font-heading text-3xl text-foreground-50 md:text-4xl">{anime.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-400">
                  <span>{anime.year} 年</span>
                  <span>全 {anime.episodes} 集</span>
                  <span><i className="ri-eye-line mr-1" />{anime.views} 播放</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {anime.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-800/80 px-3 py-1 text-xs text-foreground-300">#{t}</span>
                  ))}
                </div>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-300">{anime.desc}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button onClick={() => { setTab("watch"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-primary">
                    <i className="ri-play-fill mr-1" /> 立即观看
                  </button>
                  <button onClick={() => { setTab("download"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-ghost">
                    <i className="ri-download-2-line mr-1" /> 高清下载
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container-site py-8 md:py-12">
          <div className="flex w-fit items-center gap-1 rounded-full border border-background-700 bg-background-900 p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key as "watch" | "download")}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  tab === t.key ? "bg-primary-500 text-background-950" : "text-foreground-300 hover:text-foreground-50"
                }`}
              >
                <i className={t.icon} /> {t.label}
              </button>
            ))}
          </div>

          {tab === "watch" ? (
            <div className="mt-6">
              <h2 className="mb-4 text-lg font-bold text-foreground-100">选集播放</h2>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10">
                {episodes.map((ep) => (
                  <button
                    key={ep}
                    onClick={() => setCopied(false)}
                    className="cursor-pointer rounded-md border border-background-800 bg-background-900 py-2.5 text-sm text-foreground-300 transition-colors hover:border-primary-500 hover:bg-primary-500/10 hover:text-primary-400"
                  >
                    第{ep}话
                  </button>
                ))}
              </div>
              <p className="mt-5 flex items-center gap-2 rounded-lg border border-background-800 bg-background-900/50 px-4 py-3 text-sm text-foreground-400">
                <i className="ri-information-line text-primary-400" />
                点击对应集数即可开始免费在线播放，支持倍速与清晰度切换，无需注册登录。
              </p>
            </div>
          ) : (
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <h2 className="mb-4 text-lg font-bold text-foreground-100">选择清晰度下载</h2>
                <div className="flex flex-wrap gap-2">
                  {["4K", "1080P", "720P", "480P"].map((q) => (
                    <button
                      key={q}
                      onClick={() => setQuality(q)}
                      className={`cursor-pointer rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        quality === q ? "bg-primary-500 text-background-950" : "border border-background-700 text-foreground-300 hover:border-primary-500"
                      }`}
                    >
                      {q}
                    </button>
                  ))}
                </div>

                <div className="mt-5 space-y-3">
                  {episodes.slice(0, 8).map((ep) => (
                    <div key={ep} className="flex items-center justify-between rounded-lg border border-background-800 bg-background-900 px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground-100">{anime.title} 第{ep}话</p>
                        <p className="mt-0.5 text-xs text-foreground-500">{quality} · 内嵌字幕 · MP4</p>
                      </div>
                      <button onClick={copyLink} className="btn-ghost !px-3 !py-1.5 text-xs">
                        <i className="ri-link mr-1" /> 复制直链
                      </button>
                    </div>
                  ))}
                  {episodes.length > 8 && (
                    <p className="text-center text-xs text-foreground-500">更多集数资源请在页面内滚动选择下载</p>
                  )}
                </div>

                {copied && (
                  <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-500/15 px-4 py-2.5 text-sm font-medium text-primary-400">
                    <i className="ri-checkbox-circle-fill" /> 下载直链已复制，粘贴到下载工具即可保存
                  </p>
                )}
              </div>

              <aside className="rounded-xl border border-background-800 bg-background-900/60 p-5">
                <h3 className="flex items-center gap-2 text-base font-bold text-foreground-100">
                  <i className="ri-file-download-line text-primary-400" /> 下载说明
                </h3>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-400">
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-400" />复制直链后使用下载工具或浏览器离线下载。</li>
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-400" />支持 4K / 1080P 高清画质，全集资源持续整理。</li>
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-400" />下载前请确认设备存储空间与网络稳定。</li>
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-400" />若链接失效，可在下方「相关推荐」或列表页重试。</li>
                </ul>
              </aside>
            </div>
          )}
        </section>

        <section className="border-t border-background-800 bg-background-900/40 py-10 md:py-12">
          <div className="container-site">
            <h2 className="mb-6 flex items-center gap-2 font-heading text-2xl text-foreground-50">
              <i className="ri-stack-line text-primary-500" /> 相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:gap-4">
              {fallback.map((a) => (
                <AnimeCard key={a.id} anime={a} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}